import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FormDrawer from "src/components/Form/FormDrawer";
import { updateSpesificationSaga } from "src/store/web-spesifications/saga";
import {
  SpesificationDetailDto,
  WebModuleSliderDto,
} from "src/store/web-spesifications/type";
import * as Yup from "yup";
import { dripiconlist } from "src/helpers/iconList";
import { SectorDto } from "src/store/system/type";
import { getSectors } from "src/store/system/saga";
import ImagePreviewForUpload from "src/components/Form/imagePreview";
import { imageUrl } from "src/request/types";

const SpesificationsEditForm = ({
  data,
  onHide,
  open,
  onSuccessClick,
}: {
  data: SpesificationDetailDto;
  onSuccessClick: (message: string) => void;
  onHide: () => void;
  open: boolean;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [sectors, setSectors] = useState<SectorDto[]>([]);
  useEffect(() => {
    open &&
      dispatch(
        getSectors({
          payload: {
            onSuccess: (m, p) => setSectors(p),
            onError: () => {},
          },
        })
      );
  }, [dispatch, open]);
  return (
    <React.Fragment>
      <FormDrawer
        drawerProps={{
          isOpen: open,
          onHide: onHide,
          title: t("Spesifications.EditFormTitle").toString(),
        }}
        onHide={onHide}
        loader
        initialValues={{
          Id: data.Id,
          Title: data.Title,
          Description: data.Description,
          Icon: data.Icon,
          ShowWeb: data.ShowWeb,
          VideoLink: data.VideoLink ?? "",
          SectorId: data.SectorId ?? "",
          Name: data.Name ?? "",
          ImageFile: "",
          Images: [],
          ImageFileField: [data.Image ?? ""],
          SeoTitle: data.SeoTitle ?? "",
          SeoDescription: data.SeoDescription ?? "",
          SeoImageFile: "",
          SeoImageFileField: [data.SeoImage ?? ""],
          WebModuleSliders: data.WebModuleSliders ?? [],
          Translates: data.Translates.map((x) => {
            return {
              LanguageId: x.LanguageId,
              Title: x.Title,
              Description: x.Description,
              Name: x.Name ?? "",
              SeoTitle: x.SeoTitle ?? "",
              SeoDescription: x.SeoDescription ?? "",
            };
          }),
        }}
        validationSchema={Yup.object().shape({
          Title: Yup.string().required(t("Validation.Required").toString()),
          Name: Yup.string().required(t("Validation.Required").toString()),
          Description: Yup.string().required(
            t("Validation.Required").toString()
          ),
          Icon: Yup.string().required(t("Validation.Required").toString()),
          Translates: Yup.array(
            Yup.object({
              Name: Yup.string().required(t("Validation.Required").toString()),
              Title: Yup.string().required(t("Validation.Required").toString()),
              Description: Yup.string().required(
                t("Validation.Required").toString()
              ),
            })
          ),
        })}
        onSubmit={(values) => {
          dispatch(
            updateSpesificationSaga({
              payload: {
                onSuccess: (message, payload) => {
                  onHide();
                  onSuccessClick(message);
                },
                onError: (message) => {
                  toast.error(message, {
                    theme: "colored",
                  });
                },
              },
              id: data.Id,
              body: values,
            })
          );
        }}
        multilanguageinputs={[
          {
            field: "Name",
            label: t("Spesifications.Name").toString(),
          },
          {
            field: "Title",
            label: t("Spesifications.Title").toString(),
            type: "textarea",
          },
          {
            field: "Description",
            label: t("Spesifications.Description").toString(),
            isEditor: true,
          },
          {
            field: "SeoTitle",
            label: t("Spesifications.SeoTitle").toString(),
            type: "textarea",
          },
          {
            field: "SeoDescription",
            label: t("Spesifications.SeoDescription").toString(),
            type: "textarea",
          },
        ]}
        inputs={[
          {
            field: "SectorId",
            placement: "top",
            label: t("Spesifications.Sector").toString(),
            type: "select",
            lookup: {
              labelKey: "Name",
              valueKey: "Id",
              options: sectors,
            },
          },
          {
            field: "Icon",
            placement: "top",
            label: t("Spesifications.Icon").toString(),
            type: "select",
            lookup: {
              renderValue: (e) => <i className={e} />,
              labelKey: "Id",
              valueKey: "Id",
              options: dripiconlist.map((x, i) => {
                return {
                  Id: x,
                  Name: x,
                };
              }),
            },
          },
          {
            field: "SeoImageFile",
            label: t("Sector.SeoImageFile").toString(),
            type: "file",
            fileField: "SeoImageFileField",
            accept: "image/png, image/jpeg",

            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.SeoImageFile ? (
                    <div>
                      <ImagePreviewForUpload
                        src={URL.createObjectURL(values.SeoImageFile)}
                        width={85}
                        height={45}
                      />
                    </div>
                  ) : (
                    <ImagePreviewForUpload
                      src={imageUrl + values.SeoImageFileField}
                      width={85}
                      height={45}
                    />
                  )}
                </div>
              );
            },
          },
          {
            field: "ImageFile",
            label: t("Spesifications.ImageFile").toString(),
            type: "file",
            fileField: "ImageFileField",
            accept: "image/png, image/jpeg",

            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.ImageFile ? (
                    <div>
                      <ImagePreviewForUpload
                        src={URL.createObjectURL(values.ImageFile)}
                        width={85}
                        height={45}
                      />
                    </div>
                  ) : (
                    <ImagePreviewForUpload
                      src={imageUrl + values.ImageFileField}
                      width={85}
                      height={45}
                    />
                  )}
                </div>
              );
            },
          },

          {
            field: "Images",
            label: t("Spesifications.SliderImages").toString(),
            type: "file",
            multiple: true,
            overrideOnChange: (e, values, setFieldValue) => {
              setFieldValue("Images", [...values.Images, ...e]);
            },
            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.Images.map((x: File, i: number) => {
                    var url = URL.createObjectURL(x);
                    return (
                      <div key={i}>
                        <ImagePreviewForUpload
                          src={url}
                          width={85}
                          height={45}
                          onDelete={() => {
                            setFieldValue &&
                              setFieldValue(
                                "Images",
                                values.Images.filter(
                                  (y: File) => URL.createObjectURL(y) !== url
                                )
                              );
                          }}
                        />
                      </div>
                    );
                  })}
                  {values.WebModuleSliders.map((x: WebModuleSliderDto) => (
                    <div key={x.Id}>
                      <ImagePreviewForUpload
                        src={imageUrl + x.Path}
                        width={85}
                        height={45}
                        onDelete={() => {
                          setFieldValue &&
                            setFieldValue(
                              "SectorSliders",
                              values.SectorSliders.filter(
                                (y: any) => y.Id !== x.Id
                              )
                            );
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            },
          },
          {
            field: "ShowWeb",
            label: t("Spesifications.ShowWeb").toString(),
            type: "switch",
          },
        ]}
      />
    </React.Fragment>
  );
};
export default SpesificationsEditForm;
